import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App as AntApp,
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Switch,
  Typography
} from 'antd'
import { UserPlus } from 'lucide-react'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceApp } from 'src/api/facade/types'
import {
  applicationsApplicationsPartialUpdateMutation,
  applicationsApplicationsRetrieveOptions,
  applicationsRemoveDestroyMutation,
  organizationsRolesListOptions
} from 'src/api/main'
import type { IconPickerValue } from 'src/components'
import { useAttachmentUpload } from 'src/hooks'
import { normalizeListResults, useShowApiErrors } from 'src/utils'

import { THIRD_PARTY_CUSTOM_APP_TYPE } from '../constants/constants'
import { buildHttpUrlRule } from '../utils/appFormValidators'
import { appIconPatchBody, appIconPickerValue } from '../utils/appIcon'
import {
  invalidateWorkspaceApplicationDetailQueries,
  invalidateWorkspaceApplicationsListQueries
} from '../utils/invalidateWorkspaceApplicationQueries'
import type { ExtendedPatchedApplicationUpdate } from '../utils/thirdPartyApi'

import { AppIconField } from './AppIconField'

const { Text } = Typography

type Props = {
  app: WorkspaceApp
  workspaceId: number
  onDeleted?: () => void
}

type GeneralFormValues = {
  name: string
  description?: string
  appUrl?: string
  domain?: string
  icon?: IconPickerValue | null
}

type AutoProvisionFormValues = {
  autoProvision?: boolean
  autoProvisionRole?: number
}

/** Roles are per-workspace, and a workspace can hold a lot of them. */
const ROLES_PER_PAGE = 200

export const AppDetailSettingsTab = ({
  app,
  workspaceId,
  onDeleted
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntApp.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const { upload } = useAttachmentUpload()

  const [form] = Form.useForm<GeneralFormValues>()

  const applicationId = Number(app.id)
  const idValid = Number.isFinite(applicationId) && applicationId > 0
  const isCustom = app.instanceTypeCode === THIRD_PARTY_CUSTOM_APP_TYPE
  const isIconEditable = isCustom

  const { data: appDetailRecord } = useQuery({
    ...applicationsApplicationsRetrieveOptions({
      path: { id: applicationId }
    }),
    enabled: idValid && isCustom
  })

  // Auto-provisioning is expressed by the role alone on the backend
  // (`auto_provision_role`), so the switch here is "a role is set".
  const [autoProvisionForm] = Form.useForm<AutoProvisionFormValues>()
  const autoProvisionEnabled = Form.useWatch('autoProvision', autoProvisionForm)

  useEffect(() => {
    autoProvisionForm.setFieldsValue({
      autoProvision: app.autoProvisionRoleId != null,
      autoProvisionRole: app.autoProvisionRoleId ?? undefined
    })
  }, [app.autoProvisionRoleId, autoProvisionForm])

  const rolesQuery = useQuery({
    ...organizationsRolesListOptions({
      query: {
        application: applicationId,
        organization: workspaceId,
        per_page: ROLES_PER_PAGE
      }
    }),
    enabled: idValid && workspaceId > 0
  })
  // Workspace admins already get the app's admin role when it is installed, so
  // handing an admin role to every member would be a footgun, not a default.
  const roleOptions = useMemo(
    () =>
      normalizeListResults(rolesQuery.data)
        .filter((role) => !role.is_admin)
        .map((role) => ({ value: role.id, label: role.name })),
    [rolesQuery.data]
  )

  useEffect(() => {
    form.setFieldsValue({
      name: app.name,
      description: app.description || undefined,
      appUrl: app.appUrl ?? undefined,
      domain: app.domain ?? undefined,
      icon: appIconPickerValue(app)
    })
  }, [app, form])

  const { mutateAsync: patchApplication, isPending: isSaving } = useMutation({
    ...applicationsApplicationsPartialUpdateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.apps.messages.updated'))
      await Promise.all([
        invalidateWorkspaceApplicationDetailQueries(queryClient, applicationId),
        invalidateWorkspaceApplicationsListQueries(queryClient, workspaceId)
      ])
    },
    onError: (err) => showApiErrors(err)
  })

  const { mutateAsync: deleteApplication, isPending: isDeleting } = useMutation(
    {
      ...applicationsRemoveDestroyMutation(),
      onSuccess: async () => {
        message.success(t('workspace.apps.messages.deleted'))
        await invalidateWorkspaceApplicationsListQueries(
          queryClient,
          workspaceId
        )
        onDeleted?.()
      },
      onError: (err) => showApiErrors(err)
    }
  )

  const handleSubmit = useCallback(
    async (values: GeneralFormValues) => {
      if (!idValid) {
        return
      }
      let iconPatch: ExtendedPatchedApplicationUpdate = {}
      if (isIconEditable) {
        try {
          iconPatch = await appIconPatchBody(
            values.icon,
            (file) => upload(file, workspaceId, { isPublic: true }),
            app.settings
          )
        } catch (err) {
          showApiErrors(err)

          return
        }
      }
      const body: ExtendedPatchedApplicationUpdate = {
        name: values.name,
        description: values.description?.trim() || undefined,
        ...iconPatch
      }
      if (isCustom) {
        const trimmedAppUrl = (values.appUrl ?? '').trim()
        body.app_url = trimmedAppUrl ? trimmedAppUrl : null
        const trimmedDomain = (values.domain ?? '').trim()
        body.domain = trimmedDomain ? trimmedDomain : null
      }
      await patchApplication({
        path: { id: applicationId },
        // `settings` is missing from the generated PatchedApplicationUpdate —
        // see ExtendedPatchedApplicationUpdate. Cast intentionally.
        body: body as never
      })
    },
    [
      app.settings,
      applicationId,
      idValid,
      isCustom,
      isIconEditable,
      patchApplication,
      showApiErrors,
      upload,
      workspaceId
    ]
  )

  const handleSaveAutoProvision = useCallback(
    async (values: AutoProvisionFormValues) => {
      if (!idValid) {
        return
      }
      // Validated here rather than with a Form rule: the row-level Form.Item is
      // `noStyle`, so it has nowhere to render an inline error.
      if (values.autoProvision && values.autoProvisionRole == null) {
        message.error(t('workspace.apps.autoProvision.roleRequired'))

        return
      }
      await patchApplication({
        path: { id: applicationId },
        body: {
          // Clearing the role is how auto-provisioning is switched off; members
          // who already have access keep it.
          auto_provision_role: values.autoProvision
            ? (values.autoProvisionRole ?? null)
            : null
        }
      })
    },
    [applicationId, idValid, message, patchApplication, t]
  )

  const handleDelete = useCallback(async () => {
    const token = appDetailRecord?.token
    if (!token) {
      message.error(t('workspace.apps.messages.deleteTokenMissing'))

      return
    }
    await deleteApplication({ path: { app_token: token } })
  }, [appDetailRecord?.token, deleteApplication, message, t])

  return (
    <Flex vertical gap={14}>
      {/* General */}
      <Card
        variant="outlined"
        title={t('workspace.apps.settingsTab.general.title')}
        size="small"
      >
        <Form<GeneralFormValues>
          form={form}
          layout="vertical"
          requiredMark="optional"
          onFinish={(values) => void handleSubmit(values)}
          disabled={isSaving}
        >
          <Row gutter={14}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('workspace.apps.settingsTab.general.name')}
                name="name"
                required
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t('required_field_error')
                  }
                ]}
              >
                <Input placeholder={t('placeholders.appName')} />
              </Form.Item>
            </Col>
            {isIconEditable ? (
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('workspace.apps.settingsTab.general.icon')}
                  name="icon"
                >
                  <AppIconField appType={app.instanceTypeCode} />
                </Form.Item>
              </Col>
            ) : null}
            <Col xs={24} md={12}>
              {isCustom ? (
                <Form.Item
                  label={t('workspace.apps.settingsTab.general.appUrl')}
                  name="appUrl"
                  rules={[
                    buildHttpUrlRule(
                      t(
                        'workspace.apps.custom.fields.urlInvalid',
                        'Enter a valid URL starting with http:// or https://'
                      )
                    )
                  ]}
                  extra={t(
                    'workspace.apps.settingsTab.general.appUrlExtra',
                    'Public launch URL for the app. http:// or https:// are both accepted.'
                  )}
                >
                  <Input placeholder="https://app.example.com" />
                </Form.Item>
              ) : (
                <Form.Item
                  label={t('workspace.apps.settingsTab.general.domain')}
                  extra={t('workspace.apps.settingsTab.general.domainReadOnly')}
                >
                  <Input value={app.domain ?? ''} disabled />
                </Form.Item>
              )}
            </Col>
            {isCustom ? (
              <Col xs={24} md={12}>
                <Form.Item
                  label={t(
                    'workspace.apps.settingsTab.general.domain',
                    'Application domain'
                  )}
                  name="domain"
                  extra={t(
                    'workspace.apps.settingsTab.general.domainExtra',
                    'Optional. Authoritative host used for redirect/CORS scoping (e.g. example.com).'
                  )}
                >
                  <Input prefix="https://" placeholder="example.com" />
                </Form.Item>
              </Col>
            ) : null}
            <Col span={24}>
              <Form.Item
                label={t('workspace.apps.settingsTab.general.description')}
                name="description"
              >
                <Input.TextArea
                  rows={3}
                  placeholder={t('placeholders.description')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Flex justify="flex-end" gap={8}>
            <Button
              size="small"
              onClick={() => form.resetFields()}
              disabled={isSaving}
            >
              {t('workspace.apps.settingsTab.reset')}
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={() => form.submit()}
              loading={isSaving}
            >
              {t('workspace.apps.settingsTab.save')}
            </Button>
          </Flex>
        </Form>
      </Card>

      {/* Auto-provisioning: one role hands the app to the whole workspace */}
      <Card
        variant="outlined"
        title={t('workspace.apps.autoProvision.title')}
        size="small"
      >
        <Form<AutoProvisionFormValues>
          form={autoProvisionForm}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => void handleSaveAutoProvision(values)}
          disabled={isSaving}
        >
          {/* One row: what it does on the left, role + switch + save on the right. */}
          <Flex align="center" justify="space-between" gap={12} wrap="wrap">
            <Flex align="center" gap={10} style={{ minWidth: 0, flex: 1 }}>
              <span
                aria-hidden
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 5,
                  background: 'var(--ant-color-primary-bg)',
                  color: 'var(--ant-color-primary)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <UserPlus size={14} />
              </span>
              <Flex vertical style={{ minWidth: 0, lineHeight: 1.3 }}>
                <Text strong style={{ fontSize: 13 }}>
                  {t('workspace.apps.autoProvision.enableLabel')}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t('workspace.apps.autoProvision.description')}
                </Text>
              </Flex>
            </Flex>
            <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
              {autoProvisionEnabled ? (
                <Form.Item name="autoProvisionRole" noStyle>
                  <Select
                    size="small"
                    style={{ width: 190 }}
                    options={roleOptions}
                    loading={rolesQuery.isLoading}
                    showSearch={{ optionFilterProp: 'label' }}
                    placeholder={t('workspace.apps.autoProvision.roleLabel')}
                  />
                </Form.Item>
              ) : null}
              <Form.Item name="autoProvision" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
              <Button
                type="primary"
                size="small"
                loading={isSaving}
                onClick={() => autoProvisionForm.submit()}
              >
                {t('workspace.apps.settingsTab.save')}
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Card>

      {/* Danger zone — only third-party custom apps can be deleted from here, so
          for built-ins there is no destructive action to offer. */}
      {isCustom ? (
        <Card
          variant="outlined"
          size="small"
          title={
            <Text strong style={{ color: 'var(--ant-color-error)' }}>
              {t('workspace.apps.settingsTab.danger.title')}
            </Text>
          }
        >
          <Flex
            align="center"
            justify="space-between"
            gap={12}
            style={{ paddingBlock: 6 }}
          >
            <Flex vertical style={{ lineHeight: 1.3 }}>
              <Text strong style={{ fontSize: 13 }}>
                {t('workspace.apps.settingsTab.danger.uninstall.title')}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t('workspace.apps.settingsTab.danger.uninstall.description')}
              </Text>
            </Flex>
            <Popconfirm
              title={t('workspace.apps.custom.deleteConfirm.title')}
              description={t('workspace.apps.custom.deleteConfirm.description')}
              okText={t('workspace.apps.custom.delete')}
              okButtonProps={{ danger: true, loading: isDeleting }}
              cancelText={t('cancel')}
              onConfirm={() => void handleDelete()}
              disabled={!appDetailRecord?.token}
            >
              <Button
                size="small"
                danger
                loading={isDeleting}
                disabled={!appDetailRecord?.token}
              >
                {t('workspace.apps.settingsTab.danger.uninstall.action')}
              </Button>
            </Popconfirm>
          </Flex>
        </Card>
      ) : null}
    </Flex>
  )
}
